import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { API, TemplateGroupDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { ActivatedRoute } from '@angular/router';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit' | 'template';

@Component({
  selector: 'sb-page-templateGroup',
  templateUrl: './template-groups.component.html',
  styleUrls: ['./template-groups.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateGroupsComponent extends RequestClass implements ModalNavigationComponent {
  public readonly util = inject(UtilService);
  public readonly route = inject(ActivatedRoute);
  public readonly modal = inject(ModalNavigationService);
  public readonly permissionService = inject(PermissionService);
  private readonly api = inject(API);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  isInModalContext: boolean;

  // table wrapper variables
  templateGroups: TemplateGroupDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .mm_templating_provider_TemplateGroup_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (templateGroups: TemplateGroupDTO[]) => {
          this.templateGroups = this.buildSplitButtons(templateGroups);
        },
      });
    this.subs.push(sub);
  }

  selectTemplateGroup(group: TemplateGroupDTO) {
    if (this.isInModalContext) {
      this.modal.result(group);
    }
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.template_group_list])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.templateGroupsEdit,
          pathParams: [{ id: 'templateGroupID', value: item?.id }],
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.template_source_list])) {
        item.sbButtons.push({
          icon: 'template',
          tooltip: 'lang.globals.templateSource.show',
          link: RoutingID.templateSources,
          queryParams: { templateGroupID: item?.id },
        });
      }
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
